import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaBook, FaChartBar } from 'react-icons/fa';
import HelpModal from '../HelpModal';
import diagram_manmade_01 from './assets/diagram_manmade_01.png';
import diagram_natural_01 from './assets/diagram_natural_01.jpeg';
import diagram_natural_02 from './assets/diagram_natural_02.jpg';
import { useTextHighlight } from '../utils/highlightUtils';

function WritingDiagrams() {
    const location = useLocation();
    const navigate = useNavigate();
    const [currentPractice, setCurrentPractice] = useState(null);
    const [wordCount, setWordCount] = useState(0);
    const [answer, setAnswer] = useState('');
    const [timer, setTimer] = useState(60 * 60); // 60 মিনিট
    const timerRef = useRef();
    const [showHelp, setShowHelp] = useState(false);
    const [showSeconds, setShowSeconds] = useState(false);

    const highlightRef = useRef(null);
    useTextHighlight(highlightRef);

    useEffect(() => {
        if (location.state?.target) {
            setCurrentPractice(location.state.target);
        }
    }, [location.state]);

    
    const practices = [
        {
            id: 'practice-01-manmade',
            title: 'Practice 01 (Man-made Process)',
            description: 'Diagram Practice - Man-made Process Analysis',
            content: 'The diagram below shows how bricks are made for the building industry.',
            question: 'Summarise the information by selecting and reporting the main features, and make comparisons where relevant.',
            image: diagram_manmade_01
        },
        {
            id: 'practice-01-natural',
            title: 'Practice 01 (Natural Process)',
            description: 'Diagram Practice - Natural Process Analysis',
            content: 'The diagram below shows the lifecycle of the common frog.',
            question: 'Summarise the information by selecting and reporting the main features, and make comparisons where relevant.',
            image: diagram_natural_01
        },
        {
            id: 'practice-02-natural',
            title: 'Practice 02 (Natural Process)',
            description: 'Diagram Practice - Natural Process Analysis',
            content: 'The diagram below shows the carbon cycle in nature.',
            question: 'Summarise the information by selecting and reporting the main features, and make comparisons where relevant.',
            image: diagram_natural_02
        }
    ];

    const currentPracticeData = practices.find(p => p.id === currentPractice);
    useEffect(() => {
        if (!currentPracticeData) return;
        setTimer(60 * 60); // নতুন প্র্যাকটিসে timer reset
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [currentPracticeData]);  

    const handleTextChange = (e) => {
        const text = e.target.value;
        setAnswer(text);
        const words = text.trim().split(/\s+/).filter(word => word.length > 0);
        setWordCount(words.length);
    };
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (showSeconds) {
            return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
        }
        return `${mins} minutes remaining`;
    };
    return (
        <div ref={highlightRef} className="min-h-screen bg-[#E3E8F2]">
            {/* Help Modal */}
            <HelpModal
                isOpen={showHelp}
                onClose={() => setShowHelp(false)}
            />
            {/* Top Bar (fixed) */}
            <div className="sticky top-0 z-30 bg-white">
                <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
                    {/* Left + Center combined on left side */}
                    <div className="flex flex-col justify-center space-y-0.5 pl-6">
                        <span className="text-[14px] text-left text-black font-bold leading-tight">
                            HEXA'S Education
                        </span>
                        <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
                            <span
                                onMouseEnter={() => setShowSeconds(true)}
                                onMouseLeave={() => setShowSeconds(false)}
                                className="cursor-pointer"
                            >
                                {formatTime(timer)}
                            </span>
                        </div>
                    </div>
                    {/* Right: Icons and Finish Button */}
                    <div className="flex items-center space-x-5">
                        <button
                            onClick={() => navigate('/')}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Back Home
                        </button>
                        <button
                            onClick={() => setShowHelp(true)}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Finish test
                        </button>
                        <div className="flex items-center space-x-8 pl-2 pr-2">
                            {/* WiFi Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            {/* Notification Bell Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                            </div>
                            {/* Hamburger Menu Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            {/* Pen/Edit Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Practice Selection */}
            {!currentPracticeData && (
                <div className="p-8">
                    <div className="bg-white/95 rounded-xl shadow-lg p-6 mb-8 backdrop-blur-md">
                        <div className="flex items-center gap-4 mb-4">
                            <FaChartBar className="text-3xl text-[#001670]" />
                            <div>
                                <h1 className="text-3xl font-bold text-[#001670]">Writing Module: Diagrams</h1>
                                <p className="text-gray-600 mt-2">Class 25: Task 01 - Diagrams Practice</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {practices.map((practice) => (
                            <div
                                key={practice.id}
                                className="bg-white rounded-xl shadow-lg p-6 cursor-pointer transition-all hover:shadow-xl"
                                onClick={() => setCurrentPractice(practice.id)}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <FaBook className="text-2xl text-[#001670]" />
                                    <h3 className="text-xl font-semibold text-[#001670]">{practice.title}</h3>
                                </div>
                                <p className="text-gray-600">{practice.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Practice Content */}
            {currentPracticeData && (
                <div className="flex flex-col w-full">
                    {/* Title Box */}
                    <div className="bg-[#F4F7FA] shadow-sm border border-gray-300 mx-2 mt-4 mb-4">
                        <div className="w-full px-6 py-2 text-left">
                            <h1 className="text-[18px] font-bold text-black mb-1">{currentPracticeData.title}</h1>
                            <div className="text-[15px] text-black">
                                You should spend about 20 minutes on this task. Write at least 150 words.
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex h-[calc(100vh-220px)] mt-0 gap-4 mb-8 mx-2">
                        {/* Left Panel - Question */}
                        <div className="w-1/2 bg-[#F0F4F8] rounded-lg shadow-sm flex flex-col">
                            <div className="flex-1 overflow-y-auto px-6 py-4" style={{ scrollbarWidth: '12px', scrollbarColor: '#CBD5E1 transparent' }}>
                                <div className="text-[14px] space-y-6 text-left">
                                    <p className="text-black leading-relaxed font-bold">
                                        {currentPracticeData.content}
                                    </p>

                                    <p className="text-black leading-relaxed font-bold">
                                        Summarise the information by selecting and reporting the main features, and make comparisons where relevant.
                                    </p>

                                    <div className="w-[600px] border border-[#D1D9E1] rounded-lg overflow-hidden">
                                        <img 
                                            src={currentPracticeData.image} 
                                            alt="Diagram for practice" 
                                            className="w-full h-auto" 
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Panel - Answer Area */}
                        <div className="w-1/2 bg-white rounded-lg shadow-sm flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <div className="flex-1 p-4">
                                    <textarea
                                        className="w-full h-full resize-none p-4 text-[14px] leading-relaxed focus:outline-none bg-white"
                                        placeholder="Write your answer here..."
                                        value={answer}
                                        onChange={handleTextChange}
                                        style={{ scrollbarWidth: '12px', scrollbarColor: '#CBD5E1 transparent' }}
                                    />
                                </div>

                                {/* Word Count Bar */}
                                <div className="h-[32px] bg-[#F0F4F8] flex items-center justify-between px-4 rounded-b-lg">
                                    <div className="text-[12px] text-[#666666] font-medium">
                                        Word count: {wordCount}
                                    </div>

                                    {/* Practice Navigation */}
                                    <div className="flex items-center">
                                        {practices.map((practice, index) => (
                                            <button
                                                key={practice.id}
                                                onClick={() => setCurrentPractice(practice.id)}
                                                className={`w-[24px] h-[24px] flex items-center justify-center text-[12px] font-medium rounded ml-1 ${
                                                    currentPractice === practice.id 
                                                        ? 'bg-[#87CEEB] text-black' 
                                                        : 'bg-[#F0F4F8] text-[#666666]'
                                                }`}
                                            >
                                                {index + 1}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default WritingDiagrams; 